import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import VideoWindow from './VideoWindow';
import DockBar from './DockBar';
import PlayerPanel from './PlayerPanel';
import './playerChrome.css';
import { theme } from '../../ui/theme';

/**
 * 统一管理浮动渲染：视频浮窗 + 主播放器面板 + dock
 * 注意：VideoWindow / DockBar 内部也会 createPortal 到 body，这里仅做组合与主面板 portal。
 */
export default function PlayerPortals({
  // outer wrapper
  playerRef,
  audioRef,
  isFullscreen,
  playing,
  autoHidden,
  manualHidden,
  isLoading,
  onMouseMove,
  onMouseEnter,
  onMouseLeave,
  onBlankClick,

  // video window props
  videoWindowProps,

  // panel props (used by PlayerPanel)
  panelProps,

  // dock props
  dockProps
}) {
  const rafRef = useRef(0);
  const pointerActiveRef = useRef(false);
  const lastEventRef = useRef(null);

  const applyPointerVars = (e) => {
    const el = playerRef?.current;
    if (!el || !e) return;
    try {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX ?? 0) - rect.left;
      const y = (e.clientY ?? 0) - rect.top;
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const dx = x - cx;
      const dy = y - cy;

      // 指针相对位置（百分比）：用于 radial-gradient 的 at 锚点
      const px = (x / rect.width) * 100;
      const py = (y / rect.height) * 100;

      // 计算鼠标到各边缘的距离（像素）
      const distTop = y;
      const distBottom = rect.height - y;
      const distLeft = x;
      const distRight = rect.width - x;
      
      // 计算到最远边缘的距离，用于确定渐变应该覆盖的范围
      const maxDist = Math.max(distTop, distBottom, distLeft, distRight);
      
      // 渐变大小：从鼠标位置到最远边缘，确保能覆盖整个元素
      // 使用像素值转换为百分比（相对于元素对角线）
      const diagonal = Math.sqrt(rect.width * rect.width + rect.height * rect.height);
      const gradientSize = (maxDist / diagonal) * 100 * 2; // *2 确保覆盖整个元素
      
      // angle：与示例一致（+90，让 0deg 从上方开始）
      let angle = 0;
      if (dx !== 0 || dy !== 0) {
        angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
        if (angle < 0) angle += 360;
      }

      // edge closeness：中心=0，边缘=1
      let kx = Infinity;
      let ky = Infinity;
      if (dx !== 0) kx = cx / Math.abs(dx);
      if (dy !== 0) ky = cy / Math.abs(dy);
      const edge = Math.min(1, Math.max(0, (1 / Math.min(kx, ky))));
      const edgePct = edge * 100;

      el.style.setProperty('--pointer-x', `${px.toFixed(2)}%`);
      el.style.setProperty('--pointer-y', `${py.toFixed(2)}%`);
      el.style.setProperty('--pointer-°', `${angle.toFixed(2)}deg`);
      el.style.setProperty('--pointer-d', `${edgePct.toFixed(2)}`);
      el.style.setProperty('--gradient-size', `${gradientSize.toFixed(2)}%`);
    } catch (_) {}
  };

  // pointer move：既用于 UI 浮层，也顺便触发 “bump”
  const onPointerMove = (e) => {
    lastEventRef.current = e;
    pointerActiveRef.current = true;
    const el = playerRef?.current;
    if (el) el.classList.add('tenryu-player-shell--hover');
    applyPointerVars(e);
    onMouseMove?.(e);
  };

  const onPointerEnter = (e) => {
    pointerActiveRef.current = true;
    const el = playerRef?.current;
    if (el) el.classList.add('tenryu-player-shell--hover');
    onMouseEnter?.(e);
    // 进入时也刷新一次，避免“第一帧无高光”
    applyPointerVars(e);
  };

  const onPointerLeave = (e) => {
    pointerActiveRef.current = false;
    const el = playerRef?.current;
    if (el) el.classList.remove('tenryu-player-shell--hover');
    // 离开后把强度降为 0，让它淡出
    if (el) el.style.setProperty('--pointer-d', '0');
    onMouseLeave?.(e);
  };

  // loading：自动让高光沿边框“转一圈”，鼠标移入则交给指针
  useEffect(() => {
    const el = playerRef?.current;
    if (!el) return;
    if (isFullscreen) return;

    const cancel = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };

    if (!isLoading) {
      cancel();
      // 退出 loading：如果不在 hover，清空强度
      if (!pointerActiveRef.current) el.style.setProperty('--pointer-d', '0');
      return;
    }

    // loading 时：确保 fx 可见（即使不 hover）
    el.classList.add('tenryu-player-shell--loading');

    const start = performance.now();
    const duration = 1600; // 一圈时长（ms）
    const tick = () => {
      // 鼠标在上面时不要抢控制权
      if (pointerActiveRef.current) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const now = performance.now();
      const t = ((now - start) % duration) / duration; // 0..1
      const angle = 110 + t * 360; // 沿边框跑一圈
      const edgePct = 88 + 10 * Math.sin(t * Math.PI * 2); // 保持接近边缘，轻微呼吸
      el.style.setProperty('--pointer-°', `${angle.toFixed(2)}deg`);
      el.style.setProperty('--pointer-d', `${edgePct.toFixed(2)}`);
      rafRef.current = requestAnimationFrame(tick);
    };

    cancel();
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancel();
      try { el.classList.remove('tenryu-player-shell--loading'); } catch (_) {}
    };
  }, [isLoading, isFullscreen, playerRef]);

  // SSR/无 document：在 hooks 之后短路返回
  if (typeof document === 'undefined') return null;

  return (
    <>
      <VideoWindow {...videoWindowProps} />

      {createPortal(
        <div
          ref={playerRef}
          className={[
            'tenryu-player-shell',
            isLoading ? 'tenryu-player-shell--loading' : '',
            isFullscreen ? 'tenryu-player-shell--fullscreen' : '',
            'text-white shadow-lg overscroll-contain relative transition-transform duration-300 ease-out',
            // 全站统一：首页玻璃暗色面板
            theme.surfaces.modalPanel,
            isFullscreen ? 'h-screen flex flex-col overflow-hidden' : ''
          ].filter(Boolean).join(' ')}
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: isFullscreen ? 'auto' : 0,
            top: isFullscreen ? 0 : 'auto',
            zIndex: 9999,
            paddingBottom: isFullscreen ? undefined : 'env(safe-area-inset-bottom)',
            // 停止后应先保持可见一段时间（useAutoHideOnStop 的 delayMs），超时后才隐藏
            // 因此：只有 autoHidden=true 时才滑走；否则即使 playing=false 也保持在视口内
            transform: !isFullscreen
              ? ((manualHidden || (!playing && autoHidden)) ? 'translateY(110%)' : 'translateY(0)')
              : undefined,
            // 隐藏时不吃事件，避免遮挡页面（但仍保留 transform 过渡动画）
            pointerEvents: (!isFullscreen && (manualHidden || (!playing && autoHidden))) ? 'none' : 'auto'
          }}
          onPointerMove={onPointerMove}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onClick={onBlankClick}
        >
          <div className="tenryu-player-edgeFx" aria-hidden="true" />
          <div className="tenryu-player-glow" aria-hidden="true" />
          <div className="tenryu-player-content">
            <audio ref={audioRef} preload="metadata" />
            <PlayerPanel {...panelProps} audioRef={audioRef} />
          </div>
        </div>,
        document.body
      )}

      <DockBar {...dockProps} />
    </>
  );
}

